import {useCallback, useState} from 'react';

import {getError} from 'src/utils/get-error';

import type {Error} from 'src/openapi/admin/models/Error';
import type {EventHookReturn} from 'src/typedef';

type Props<A, R = void> = {
    eventAction: (actionProps: A) => Promise<{
        type: string,
        payload: {
            data: R,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    onSuccess?: (data: R) => void,
    onFail?: (error: string) => void,
};

export const useCommonEventHook: {
    <A = void, R = void>(props: Props<A, R>): EventHookReturn<A>,
    <A = void>(props: Props<A, void>): EventHookReturn<A>,
    <R = void>(props: Props<void, R>): EventHookReturn<void>,
    (props: Props<void, void>): EventHookReturn<void>,
} = <A, R>({eventAction, onSuccess, onFail}: Props<A, R>): EventHookReturn<A> => {
    const [busy, setBusy] = useState(false);

    const event = useCallback<(actionProps: A) => Promise<string | null>>(async (actionProps: A) => {
        let error: string | null = null;

        setBusy(true);
        const res = await eventAction(actionProps);
        setBusy(false);

        if (res.payload.success) {
            error = null;
            if (onSuccess) {
                onSuccess(res.payload.data);
            }
        } else if (res.payload.error) {
            error = getError({error: res.payload.data, separator: ' '});
            if (onFail) {
                onFail(error);
            }
        }

        return error;
    }, [eventAction, onFail, onSuccess]);

    return {busy, event};
};
